import { createContext, useContext } from 'react'

/**
 * Что можно открыть окном поверх текущего экрана.
 *
 * Каждое окно умеет открыться только по ключу, без строки списка: список
 * страницы навигатору недоступен, нужные данные окно догружает само.
 */
export type EntityTarget =
  | { kind: 'design'; code: number | string }
  | { kind: 'product'; article: string }
  | {
      kind: 'draft'
      cardId: number
      sellerId: number
      marketplace: 'wb' | 'ozon' | 'ym'
    }
  | {
      kind: 'mp_card'
      marketplace: 'wb' | 'ozon' | 'ym' | 'site'
      /** Кабинет; у карточки сайта не нужен. */
      sellerId?: number | string | null
      /** ВБ — nmID, ОЗОН и Я.Маркет — offer_id, сайт — артикул. */
      key: string
    }

export interface EntityNavigator {
  /** Положить окно сущности поверх текущего. Повторное открытие того же окна не дублируется. */
  open: (target: EntityTarget) => void
  /** Снять верхнее окно стека: Esc, крестик и «Назад» делают одно и то же. */
  back: () => void
  /** Сколько окон навигатор держит открытыми. */
  depth: number
  /** Провайдер смонтирован: без него `open` ничего не делает. */
  available: boolean
}

const noop = () => undefined

export const FALLBACK_NAVIGATOR: EntityNavigator = {
  open: noop,
  back: noop,
  depth: 0,
  available: false,
}

export const EntityNavigatorContext = createContext<EntityNavigator>(FALLBACK_NAVIGATOR)

/**
 * Навигатор окон сущностей. Вне провайдера (отдельные тесты, страницы без
 * AppLayout) возвращает заглушку: звенья цепочки при этом остаются
 * неактивными, а не падают.
 */
export const useEntityNavigator = (): EntityNavigator => useContext(EntityNavigatorContext)

/** Одинаковые цели — одно и то же окно. */
export const sameTarget = (left: EntityTarget, right: EntityTarget): boolean => {
  if (left.kind !== right.kind) return false
  switch (left.kind) {
    case 'design':
      return String(left.code) === String((right as typeof left).code)
    case 'product':
      return left.article === (right as typeof left).article
    case 'draft':
      return (
        left.cardId === (right as typeof left).cardId &&
        left.sellerId === (right as typeof left).sellerId
      )
    case 'mp_card': {
      const other = right as typeof left
      return (
        left.marketplace === other.marketplace &&
        left.key === other.key &&
        String(left.sellerId ?? '') === String(other.sellerId ?? '')
      )
    }
    default:
      return false
  }
}
